/**
 * NewEventButton — the "+ Agregar evento" affordance on the Well record. It opens
 * a stylized `<Modal>` (house rule: create/edit forms open in a modal) to add a
 * new job to the current well, making the record the primary entry point for one.
 *
 * The form: tipo de evento (required select → drilling|workover|completion|
 * intervention), código, fecha de inicio, objetivo, pozo perforado (wellbore_uwi)
 * and perfil de unidades — the last four optional. On save it POSTs to Oxbo-P via
 * `createEvent({ well_uwi, … })`; on success it makes the new event active (the
 * event selector refetches via tag invalidation) and closes. A 422 (bad
 * event_type — not reachable from the fixed select) surfaces the generic error.
 *
 * `variant="primary"` renders the filled button used in the selector header;
 * `variant="empty"` renders the inviting prompt shown when the well has no events.
 */
import { useState } from "react";
import { useAppDispatch } from "@app/hooks";
import { setActiveEvent } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { Modal } from "@components/ui/Modal";
import { useCreateEventMutation } from "./eventEntryApi";
import type { EventCreate, EventType } from "@api/types";

const EVENT_TYPES: EventType[] = ["drilling", "workover", "completion", "intervention"];

const TYPE_LABEL: Record<EventType, Parameters<ReturnType<typeof useLocale>["t"]>[0]> = {
  drilling: "eventCreate.type.drilling",
  workover: "eventCreate.type.workover",
  completion: "eventCreate.type.completion",
  intervention: "eventCreate.type.intervention",
};

interface Draft {
  event_type: EventType;
  event_code: string;
  date_ops_start: string;
  objective_1: string;
  wellbore_uwi: string;
  unit_profile_key: string;
}

const EMPTY: Draft = {
  event_type: "drilling",
  event_code: "",
  date_ops_start: "",
  objective_1: "",
  wellbore_uwi: "",
  unit_profile_key: "",
};

export function NewEventButton({
  wellUwi,
  variant = "primary",
}: {
  wellUwi: string;
  variant?: "primary" | "empty";
}) {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [createEvent, { isLoading, error, reset }] = useCreateEventMutation();

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const openModal = () => {
    setDraft(EMPTY);
    reset();
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    reset();
  };

  async function submit() {
    const body: EventCreate = {
      well_uwi: wellUwi,
      event_type: draft.event_type,
      event_code: draft.event_code.trim() || null,
      date_ops_start: draft.date_ops_start || null,
      objective_1: draft.objective_1.trim() || null,
      wellbore_uwi: draft.wellbore_uwi.trim() || null,
      unit_profile_key: draft.unit_profile_key.trim() || null,
    };
    try {
      const event = await createEvent(body).unwrap();
      dispatch(setActiveEvent(event.id));
      setOpen(false);
    } catch {
      // Error surfaces in the modal footer; keep it open so input isn't lost.
    }
  }

  return (
    <>
      {variant === "empty" ? (
        <div className="empty-state event-empty">
          <p>{t("eventCreate.emptyPrompt")}</p>
          <button type="button" className="btn primary" onClick={openModal}>
            {t("eventCreate.button")}
          </button>
        </div>
      ) : (
        <button type="button" className="btn primary" onClick={openModal}>
          {t("eventCreate.button")}
        </button>
      )}

      <Modal
        open={open}
        title={t("eventCreate.title")}
        onClose={close}
        onConfirm={submit}
        busy={isLoading}
        canConfirm={!isLoading}
        confirmLabel={isLoading ? t("eventCreate.saving") : t("eventCreate.save")}
        cancelLabel={t("eventCreate.cancel")}
        footerNote={error ? t("eventCreate.error") : null}
      >
        <div className="modal-grid">
          <div className="field">
            <label htmlFor="ne-type">{t("eventCreate.field.type")}</label>
            <select
              id="ne-type"
              value={draft.event_type}
              onChange={(e) => set("event_type", e.target.value as EventType)}
            >
              {EVENT_TYPES.map((ty) => (
                <option key={ty} value={ty}>
                  {t(TYPE_LABEL[ty])}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ne-code">{t("eventCreate.field.code")}</label>
            <input
              id="ne-code"
              type="text"
              value={draft.event_code}
              onChange={(e) => set("event_code", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ne-start">{t("eventCreate.field.dateStart")}</label>
            <input
              id="ne-start"
              type="date"
              value={draft.date_ops_start}
              onChange={(e) => set("date_ops_start", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ne-wellbore">{t("eventCreate.field.wellbore")}</label>
            <input
              id="ne-wellbore"
              type="text"
              value={draft.wellbore_uwi}
              onChange={(e) => set("wellbore_uwi", e.target.value)}
            />
          </div>
          <div className="field modal-span-all">
            <label htmlFor="ne-objective">{t("eventCreate.field.objective")}</label>
            <input
              id="ne-objective"
              type="text"
              value={draft.objective_1}
              onChange={(e) => set("objective_1", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ne-unit">{t("eventCreate.field.unitProfile")}</label>
            <input
              id="ne-unit"
              type="text"
              value={draft.unit_profile_key}
              onChange={(e) => set("unit_profile_key", e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
